import { XMarkIcon } from '@heroicons/react/24/outline';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import React, { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';

type Size = 'xs' | 'lg' | 'xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
export interface ModalProps extends PropsWithChildren {
  className?: string;
  baseClassName?: string;
  bodyClassName?: string;
  hideFooter?: boolean;
  footer?: {
    baseClassName?: string;
    variant?: ButtonProps['variant'];
    text?: string;
    onClick?: () => void;
    left?: React.ReactElement;
    form?: string;
    className?: string;
    isLoading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    disabled?: boolean;
    'data-testid'?: string;
    hideCancelButton?: boolean;
  };
  icon?: React.ReactNode;
  onClose: () => void;
  hideClose?: boolean;
  open: boolean;
  size?: Size;
  title: ReactNode;
  subtitle?: ReactNode;
  style?: 'default' | 'dialog';
  closeOnOutsideClick?: boolean;
  divider?: boolean;
  disableAutoFocus?: boolean;
}

export const Modal: React.FC<ModalProps> = props => {
  const {
    children,
    className = '',
    baseClassName = '',
    footer = undefined,
    onClose = () => {},
    divider = false,
    subtitle = '',
    open = false,
    size = 'lg',
    title = '',
    icon,
    style = 'default',
    closeOnOutsideClick = true,
    hideFooter,
    bodyClassName = '',
    hideClose,
    disableAutoFocus = false,
  } = props;
  const { modal } = useGlobalState();
  const { onOpenChange: setModalOpen } = modal.allModal;
  const isDialog = style === 'dialog';
  const footerButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setModalOpen(open);
  }, [open]);

  useEffect(() => {
    if (open && footer?.text && !disableAutoFocus) {
      const timeoutId = setTimeout(() => {
        footerButtonRef.current?.focus();
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        footerButtonRef.current?.blur();
      };
    }
  }, [open, disableAutoFocus]);

  return (
    <ModalWrapper
      open={open}
      size={size}
      onClose={onClose}
      closeOnOutsideClick={closeOnOutsideClick}
      className={baseClassName}
    >
      <div
        data-testid="modal"
        className={cn('flex size-full flex-col', bodyClassName)}
      >
        <div>
          <div
            className={cn(
              'flex justify-between rounded-t-lg text-lg font-semibold',
              !isDialog && 'bg-chariot-background-primary py-2 pr-4 pl-6'
            )}
            data-testid="modal-title"
          >
            <div className="flex w-full items-center justify-center">
              {icon && (
                <div className="flex size-10 shrink-0 items-center justify-center">
                  {icon}
                </div>
              )}
              <div className="w-full">
                <DialogPrimitive.Title className="text-lg leading-none font-semibold tracking-tight">
                  {title}
                </DialogPrimitive.Title>
                {subtitle && (
                  <DialogPrimitive.Description className="text-chariot-text-secondary mt-1 text-sm font-normal">
                    {subtitle}
                  </DialogPrimitive.Description>
                )}
              </div>
            </div>

            {!hideClose && (
              <Button
                onClick={onClose}
                variant="ghost"
                size="icon"
                aria-label="Close"
                data-testid="modal-close-button"
              >
                <XMarkIcon className="size-6" />
              </Button>
            )}
          </div>
          {divider && (
            <div className="w-full px-6 py-2">
              <div className="bg-chariot-stroke h-px w-full" />
            </div>
          )}
          <div
            className={cn(
              'size-full overflow-auto px-6 py-2',
              isDialog && 'px-10 py-2',
              isDialog && !icon && 'px-0',
              className
            )}
            data-testid="modal-body"
          >
            {children}
          </div>
        </div>
        {!hideFooter && !(footer?.hideCancelButton && !footer?.text) && (
          <div
            className={cn(
              'flex justify-between rounded-b-lg',
              !isDialog && 'px-6 py-4 pb-5',
              isDialog && 'p-6',
              footer?.baseClassName
            )}
          >
            <div className="flex">{footer?.left}</div>
            <div className="flex gap-2">
              {!footer?.hideCancelButton ? (
                <Button
                  onClick={onClose}
                  variant="ghost"
                  className="bg-chariot-background-primary m-0! px-4"
                  data-testid="modal-cancel-button"
                >
                  Cancel
                </Button>
              ) : (
                <div></div>
              )}

              {footer?.text && (
                <Button
                  ref={footerButtonRef}
                  onClick={() => footer?.onClick?.()}
                  variant={footer.variant || 'default'}
                  type={footer?.form ? 'submit' : undefined}
                  form={footer?.form}
                  className={cn('ml-2 min-w-24 px-4', footer?.className)}
                  disabled={footer?.disabled}
                  data-testid={footer['data-testid'] || 'modal-primary-button'}
                >
                  {footer?.startIcon}
                  {footer.text || ''}
                  {footer?.endIcon}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

interface ModalWrapperProps extends PropsWithChildren {
  className?: string;
  open: boolean;
  size?: Size;
  onClose: () => void;
  closeOnOutsideClick?: boolean;
}

export const ModalWrapper: React.FC<ModalWrapperProps> = props => {
  const {
    children,
    className = '',
    onClose = () => {},
    open = false,
    size = 'lg',
    closeOnOutsideClick = true,
  } = props;

  const widthMap = {
    xs: 'max-w-xs',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'w-[95%] h-[95%]',
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100] bg-black/40 dark:bg-black/60" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onInteractOutside={e => {
            // Prevent closing when interacting with FloatingUI portals
            // (e.g., Select dropdowns rendered via FloatingPortal,
            // Dropdown menus rendered outside the Dialog DOM tree)
            if (
              document.querySelector('.Popover') ||
              document.querySelector('[data-testid="dropdown-menu"]')
            ) {
              e.preventDefault();
              return;
            }
            if (!closeOnOutsideClick) {
              e.preventDefault();
            }
          }}
          className={cn(
            'fixed top-[50%] left-[50%] z-[100] w-full translate-x-[-50%] translate-y-[-50%]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-200',
            'flex items-center justify-center p-4'
          )}
        >
          <div
            className={cn(
              `border-chariot-stroke w-full border-2 ${widthMap[size]} bg-chariot-background-primary text-chariot-text-primary max-h-[calc(100vh-2rem)] overflow-auto rounded-[10px] text-left transition-all`,
              className
            )}
          >
            <div className="size-full">{children}</div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
